import React from "react";
import { TestResult } from "../types";
import ResultCard from "./ResultCard";
import ShareResultsForm from "./ShareResultsForm";
import PremiumCTA from "./PremiumCTA";
import Button from "./Button";
import { Download, Calendar, Repeat } from "lucide-react";
import { useErrorHandling } from "../hooks/useErrorHandling";
// import { useTestValidation } from "../hooks/useTestValidation";
import FeedbackToast from "./FeedbackToast";

interface ResultsContentProps {
  result: TestResult;
  onRestart: () => void;
}

const ResultsContent: React.FC<ResultsContentProps> = ({
  result,
  onRestart,
}) => {
  const { error, handleError, clearError } = useErrorHandling();
  //  const { validateTestData } = useTestValidation();

  // console.log("Results Content Category Scores:", categoryScores);

  // Validate result data
  /*  React.useEffect(() => {
    if (!validateTestData(result, [])) {
      handleError(new Error("Invalid test results"), "ResultsContent");
    }
  }, [result, validateTestData, handleError]);*/

  const handlePrint = async () => {
    try {
      window.print();
    } catch (err) {
      handleError(err, "Print Results");
    }
  };

  const handleShareSuccess = () => {
    clearError();
  };

  // Convert percentages to array for heatmap
  /* const userScores = [
    percentages.jaguar || 0,
    percentages.condor || 0,
    percentages.llama || 0,
    percentages.quetzal || 0,
    percentages.colibri || 0,
    percentages.coqui || 0,
    percentages.buho || 0,
  ]; */

  if (!result?.primaryArchetype || !result?.secondaryArchetype) {
    return null;
  }

  return (
    <>
      <div className="mb-10">
        <ResultCard result={result} />
      </div>

      <div className="mb-12">
        <PremiumCTA
          email={result.email || ""}
          archetype={result.primaryArchetype.name}
          reportId={result.testResultId || ""}
        />
      </div>

      <div className="mb-12">
        <ShareResultsForm
          testResultId={result.testResultId || ""}
          inviterName={result.userName || ""}
          archetype={result.primaryArchetype.name}
          archetypeDescription={result.primaryArchetype.description}
          onShare={handleShareSuccess}
        />
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-12">
        <div className="bg-white p-5 rounded-lg shadow-md">
          <div className="text-orange-500 mb-3">
            <Download size={24} />
          </div>
          <h3 className="font-semibold text-lg mb-2">Guarda tus Resultados</h3>
          <p className="text-gray-600 mb-4">
            Mantén un registro de tus resultados para consultarlos durante tu planificación de carrera y conversaciones de desarrollo profesional.
          </p>
          <Button variant="outline" onClick={handlePrint} className="w-full">
            Imprimir Resultados
          </Button>
        </div>

        <div className="bg-white p-5 rounded-lg shadow-md">
          <div className="text-orange-500 mb-3">
            <Calendar size={24} />
          </div>
          <h3 className="font-semibold text-lg mb-2">Próximos Pasos</h3>
          <p className="text-gray-600 mb-3">
            Aprovecha tus fortalezas de {result.primaryArchetype.name} en tu rol actual enfocándote en tareas que se alineen con tus talentos naturales:
          </p>
          <ul className="list-disc text-gray-600 ml-5 space-y-1">
            <li>Identifica proyectos que se ajusten a tu arquetipo</li>
            <li>Comparte tus ideas con mentores o líderes</li>
            <li>Desarrolla un plan de carrera basado en tus fortalezas</li>
          </ul>
        </div>

        <div className="bg-white p-5 rounded-lg shadow-md">
          <div className="text-amber-500 mb-3">
            <Repeat size={24} />
          </div>
          <h3 className="font-semibold text-lg mb-2">Vuelve a Tomar el Test</h3>
          <p className="text-gray-600 mb-4">
            A medida que creces profesionalmente, tu arquetipo puede evolucionar. Considera retomar el test anualmente para seguir tu desarrollo.
          </p>
          <Button variant="outline" onClick={onRestart} className="w-full">
            Volver a Tomar el Test
          </Button>
        </div>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg mb-12">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">
          Aviso sobre Resultados
        </h3>
        <p className="text-gray-600 leading-relaxed">
          El <b>Test Latino™</b> es una herramienta culturalmente diseñada para la autoconciencia, creada para ayudarte a explorar cómo tus instintos naturales, valores y fortalezas se reflejan en tu vida profesional. Inspirado en siete poderosos arquetipos animales de toda América Latina, este test ofrece una nueva perspectiva para entender mejor tu estilo de liderazgo, contribuciones en el trabajo y posibilidades de carrera.
        </p>
        <p className="text-gray-600 mt-4">
          Tus resultados se basan en tus respuestas a situaciones hipotéticas y ofrecen orientación, no categorías rígidas. El <b>Test Latino™</b> no es una evaluación clínica ni un diagnóstico científico. Te animamos a usar tu arquetipo como punto de partida para el autodescubrimiento, desarrollo de liderazgo y crecimiento profesional, especialmente como latino navegando diferentes caminos de carrera.
        </p>
      </div>

      {error && (
        <FeedbackToast message={error} type="error" onClose={clearError} />
      )}
    </>
  );
};

export default ResultsContent;
